/**
 * Login-side med Microsoft OAuth
 */

import React, { useState, useEffect } from 'react';
import crmApi from '../api/crm';
import './Login.css';

export default function Login({ onLoginSuccess }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showLocalLogin, setShowLocalLogin] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  useEffect(() => {
    // Sjekk om vi kommer tilbake fra OAuth callback
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    const state = urlParams.get('state');
    const error = urlParams.get('error');

    if (error) {
      setError(`OAuth feil: ${error}`);
      // Rens URL
      window.history.replaceState({}, document.title, window.location.pathname);
      return;
    }

    if (code && state) {
      handleOAuthCallback(code, state);
    }
  }, []);

  const handleOAuthCallback = async (code, state) => {
    setLoading(true);
    setError(null);

    try {
      // Send code til backend for å bytte mot tokens
      const API_URL = process.env.REACT_APP_API_URL || 'https://advokat-crm-api.swagner.workers.dev';
      const response = await fetch(`${API_URL}/auth/callback?code=${encodeURIComponent(code)}&state=${encodeURIComponent(state)}`);

      if (!response.ok) {
        throw new Error('OAuth callback feilet');
      }

      const data = await response.json();

      // Backend returnerer 'token' og 'user'
      if (data.token) {
        // Lagre token og user
        localStorage.setItem('access_token', data.token);
        localStorage.setItem('user', JSON.stringify(data.user));

        // Rens URL
        window.history.replaceState({}, document.title, '/dashboard');

        // Kall onLoginSuccess
        onLoginSuccess(data.user);
      } else {
        throw new Error(data.error || 'Login feilet');
      }
    } catch (err) {
      console.error('OAuth callback error:', err);
      setError(`Login feilet: ${err.message}`);

      // Rens URL ved feil
      window.history.replaceState({}, document.title, window.location.pathname);
    } finally {
      setLoading(false);
    }
  };

  const handleLocalLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const API_URL = process.env.REACT_APP_API_URL || 'https://advokat-crm-api.swagner.workers.dev';
      const response = await fetch(`${API_URL}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Innlogging feilet');
      }

      const data = await response.json();

      // Lagre token og user
      localStorage.setItem('access_token', data.token);
      localStorage.setItem('user', JSON.stringify(data.user));

      // Kall onLoginSuccess
      onLoginSuccess(data.user);
    } catch (err) {
      console.error('Local login error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleMicrosoftLogin = async () => {
    setLoading(true);
    setError(null);

    try {
      // Hent OAuth URL fra backend
      const API_URL = process.env.REACT_APP_API_URL || 'https://advokat-crm-api.swagner.workers.dev';
      const response = await fetch(`${API_URL}/auth/login`);

      if (!response.ok) {
        throw new Error('Kunne ikke hente login URL');
      }

      const data = await response.json();

      if (data.authUrl) {
        // Lagre state for validering
        localStorage.setItem('oauth_state', data.state);

        // Redirect til Microsoft login
        window.location.href = data.authUrl;
      } else {
        throw new Error('Ingen authUrl mottatt');
      }
    } catch (err) {
      console.error('Microsoft login error:', err);
      setError(`Kunne ikke starte innlogging: ${err.message}`);
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="login-container">
        <div className="login-box">
          <div className="login-spinner"></div>
          <p>Logger inn...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="login-container">
      <div className="login-box">
        <div className="login-header">
          <div className="login-logo">⚖️</div>
          <h1>AdvokatCRM</h1>
          <p>Logg inn med din Microsoft-konto</p>
        </div>

        {error && (
          <div className="login-error">
            ⚠️ {error}
          </div>
        )}

        <div className="login-content">
          {!showLocalLogin ? (
            <>
              <button
                className="btn-microsoft-login"
                onClick={handleMicrosoftLogin}
                disabled={loading}
              >
                <svg className="microsoft-icon" viewBox="0 0 23 23">
                  <rect x="1" y="1" width="10" height="10" fill="#f25022"/>
                  <rect x="12" y="1" width="10" height="10" fill="#00a4ef"/>
                  <rect x="1" y="12" width="10" height="10" fill="#7fba00"/>
                  <rect x="12" y="12" width="10" height="10" fill="#ffb900"/>
                </svg>
                Logg inn med Microsoft
              </button>

              <div className="login-divider">
                <span>eller</span>
              </div>

              <button
                className="btn-local-login"
                onClick={() => setShowLocalLogin(true)}
              >
                📧 Logg inn med e-post
              </button>

              <div className="login-info">
                <p>
                  <strong>Logg inn med din Microsoft-arbeidskonto</strong><br/>
                  Dette gir deg tilgang til CRM-systemet og SharePoint-dokumenter.
                </p>
              </div>
            </>
          ) : (
            <>
              <form onSubmit={handleLocalLogin}>
                <div className="form-group">
                  <label>E-post</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="din@epost.no"
                    required
                    autoFocus
                  />
                </div>

                <div className="form-group">
                  <label>Passord</label>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    required
                  />
                </div>

                <button type="submit" className="btn-login-submit">
                  Logg inn
                </button>
              </form>

              <button
                className="btn-back"
                onClick={() => setShowLocalLogin(false)}
              >
                ← Tilbake
              </button>

              <div className="login-info">
                <p style={{ fontSize: '0.85rem', color: '#666' }}>
                  <strong>Test-bruker:</strong><br/>
                  E-post: test@test.no<br/>
                  Passord: test123
                </p>
              </div>
            </>
          )}
        </div>

        <div className="login-footer">
          <p>Nesodden Advokatkontor AS</p>
        </div>
      </div>
    </div>
  );
}
